import { useEffect } from 'react';
import { create } from 'zustand';
import TimeSheetModel from '@/models/entities/TimeSheetModel';
import { listPage, type PageRequest } from '@/models/listPage';
import { COMPANY_ROLE } from '@/models/references/references';
import { useCurrentCompany } from '@/stores/useCompanyStore';

interface PendingValidationStore {
  /** Société suivie ; null : personne à suivre (ni Dirigeant, ni manager) */
  companyId: number | null;
  /** Feuilles qui attendent l'utilisateur */
  count: number;
  follow: (companyId: number | null) => void;
  refresh: () => Promise<void>;
}

let followed: number | null | undefined;

/**
 * Feuilles de la société qui attendent la validation de l'utilisateur, total seul (GET /time_sheets?awaitingMe=true
 * &period.company=… : la règle est côté API, App\Filter\TimeSheetAwaitingMeFilter ; la Validation s'ouvre sur la même
 * liste)
 */
export function pendingRequest(companyId: number): PageRequest {
  return { filters: { awaitingMe: 'true', 'period.company': companyId }, page: 1, itemsPerPage: 1 };
}

/**
 * Pastille de « Validation » : les feuilles qui attendent l'utilisateur (manager : en attente du manager de ses équipes ;
 * Dirigeant : en attente du dirigeant, plus celles de l'équipe qu'il manage). Rechargée à chaque changement de page et
 * après une validation ou un refus, en attendant les notifications.
 */
export const usePendingValidation = create<PendingValidationStore>((set, get) => ({
  companyId: null,
  count: 0,

  follow: (companyId) => {
    if (followed === companyId) return;
    followed = companyId;
    set({ companyId, count: 0 });
  },

  refresh: async () => {
    const { companyId } = get();
    if (companyId === null) return;
    try {
      const { totalItems } = await listPage(TimeSheetModel, pendingRequest(companyId));
      if (get().companyId === companyId) set({ count: totalItems });
    } catch {
      // Indisponible : pas de pastille
    }
  },
}));

/** Suit les feuilles qui attendent l'utilisateur dans la société sélectionnée (sidebar), rechargées à chaque page */
export function useFollowPendingValidation(pathname: string): number {
  const company = useCurrentCompany();
  // Dirigeant ou manager : les seuls qui valident
  const companyId =
    company && (company.companyRole === COMPANY_ROLE.DIRECTOR || company.managedTeamIds.length) ? company.id : null;
  const { count, follow, refresh } = usePendingValidation();

  // Autre société, ou nouvelle page (le compte a pu changer : feuille soumise par un employé) : un seul chargement
  useEffect(() => {
    follow(companyId);
    void refresh();
  }, [companyId, pathname, follow, refresh]);

  return companyId === null ? 0 : count;
}
